import type { ReactNode } from "react";

export const ONE_CRORE = 1_00_00_000;

type RenderAmountOptions = {
  decimals?: 2 | 4;
  align?: "left" | "center" | "right";
  showRupee?: boolean;
};

const formatInr = (amount: number, decimals = 2) =>
  Math.abs(amount).toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

export function renderAmountCell(
  value: unknown,
  options: RenderAmountOptions = {},
): ReactNode {
  const { decimals = 2, showRupee = true } = options;
  if (value === null || value === undefined || value === "") return "-";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";

  if (Math.abs(amount) >= ONE_CRORE) {
    const cr = (amount / ONE_CRORE).toFixed(2);
    return (
      <span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
        {showRupee ? "₹" : ""}
        {cr} Cr
      </span>
    );
  }

  return (
    <span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
      {showRupee ? "₹" : ""}
      {formatInr(amount, decimals)}
    </span>
  );
}

export function renderUnitsCell(value: unknown): ReactNode {
  if (value === null || value === undefined || value === "") return "-";
  const units = Number(value);
  if (!Number.isFinite(units)) return "-";
  return (
    <span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
      {units.toLocaleString("en-IN", { maximumFractionDigits: 4 })}
    </span>
  );
}

export function renderPnLCell(gainLoss: unknown, gainLossPerc: unknown): ReactNode {
  if (gainLoss === null || gainLoss === undefined) return "-";
  const amount = Number(gainLoss);
  if (!Number.isFinite(amount)) return "-";
  const perc = Number(gainLossPerc);
  const percLabel = Number.isFinite(perc) ? `(${perc.toFixed(2)}%)` : "(-)";
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.3 }}>
      {renderAmountCell(amount)}
      <span>{percLabel}</span>
    </span>
  );
}

export function renderPnLWithPercentInline(
  gainLoss: unknown,
  gainLossPerc: unknown,
  percDecimals = 2,
): ReactNode {
  return renderPnLCell(gainLoss, gainLossPerc);
}

export function renderFundSizeWithDate(
  fundSize: unknown,
  asOfDate?: string | null,
): ReactNode {
  if (fundSize == null || fundSize === "") return "-";
  return (
    <span>
      {renderAmountCell(fundSize, { align: "right" })}
      {" Cr"}
      {asOfDate ? ` (as of ${asOfDate})` : ""}
    </span>
  );
}

export const transactionActionsClassName = "flex justify-center items-center gap-0 pl-3";
export const transactionActionIconClassName = "!p-0 !min-w-[24px] w-6 h-6";
